// Directional vision for tactical-zone play.
//
// Tactical mode adds a facing + cone-angle gate on top of a range check. This
// module owns only the cone geometry and the cone-gated target search; tactical
// enemy behavior consumes it through explicit function calls.
//
// Bearing math mirrors fog-geometry.mjs's local equirectangular projection
// (not the haversine distanceInMeters from road-graph.mjs, which has no
// notion of direction) so a "straight ahead" cone stays consistent with how
// the rest of the tactical geometry treats short, local distances.

import { distanceInMeters } from "../road-graph.mjs";
import { ringsOf, sightLineBlocked } from "./line-of-sight.mjs";

export const VISION_CONE_DEGREES = 90;
export const VISION_RANGE_METERS = 70;

// Ray-cast cone step angle in degrees; matches the density the old canvas
// renderer used (tactical-view.mjs's occludedConePolygonPx), fine enough to look
// smooth without pointlessly re-testing every occluder edge per degree.
const CONE_RAYCAST_STEP_DEGREES = 1.5;

// Ranked distraction pull within a stimulus class: bigger wins, distance
// breaks ties. Owned here (not tactical-enemy-ai.mjs) since both the cone
// search below and that module's omnidirectional contact check need it, and
// this module has no dependency on tactical-enemy-ai.mjs to avoid a cycle.
export const TARGET_PULL_WEIGHTS = Object.freeze({
  vehicle: 2,
  survivor: 1,
});

function metersPerDegreeAt(latitude) {
  return {
    metersPerLongitude: Math.cos(latitude * Math.PI / 180) * Math.PI * 6371000 / 180,
    metersPerLatitude: Math.PI * 6371000 / 180,
  };
}

function localOffsetMeters(from, to) {
  const { metersPerLongitude, metersPerLatitude } = metersPerDegreeAt((from[1] + to[1]) / 2);
  return [
    (to[0] - from[0]) * metersPerLongitude,
    (to[1] - from[1]) * metersPerLatitude,
  ];
}

// Inverse of localOffsetMeters for a single fixed origin: where does a point
// dx/dy meters (east/north) from origin land in lon/lat? Scaled off the
// origin's own latitude rather than a from/to midpoint — there's no
// destination yet to average with, and at tactical-zone scale (tens to a few
// hundred meters) that's well within the flat-earth approximation everyone
// else in this module already leans on.
function coordinateAtOffsetMeters(origin, dx, dy) {
  const { metersPerLongitude, metersPerLatitude } = metersPerDegreeAt(origin[1]);
  return [origin[0] + dx / metersPerLongitude, origin[1] + dy / metersPerLatitude];
}

// Bearing in degrees, 0 = east, 90 = north, matching atan2(dy, dx) convention
// (not compass bearing) — internal to this module, callers only ever compare
// two bearings produced by the same function, so the zero-point is arbitrary
// as long as it's consistent.
export function facingBearingDegrees(from, to) {
  const [dx, dy] = localOffsetMeters(from, to);
  if (dx === 0 && dy === 0) return 0;
  return Math.atan2(dy, dx) * 180 / Math.PI;
}

function angleDeltaDegrees(a, b) {
  let delta = (a - b) % 360;
  if (delta > 180) delta -= 360;
  if (delta < -180) delta += 360;
  return Math.abs(delta);
}

export function isWithinVisionCone(observerCoordinate, targetCoordinate, {
  facingDegrees,
  coneDegrees = VISION_CONE_DEGREES,
  rangeMeters = VISION_RANGE_METERS,
  occluders = null,
} = {}) {
  if (!Number.isFinite(facingDegrees)) return false;
  const distance = distanceInMeters(observerCoordinate, targetCoordinate);
  if (distance > rangeMeters) return false;
  // A target standing exactly on the observer has no defined bearing; treat
  // that as visible rather than excluded, since contact-range logic in
  // tactical-enemy-ai.mjs already handles adjacency separately from this check.
  if (distance === 0) return true;
  const bearing = facingBearingDegrees(observerCoordinate, targetCoordinate);
  if (angleDeltaDegrees(bearing, facingDegrees) > coneDegrees / 2) return false;
  // In cone and in range, but a building between observer and target hides it.
  if (occluders && sightLineBlocked(observerCoordinate, targetCoordinate, occluders)) return false;
  return true;
}

// Target priority for cone-gated sight. Larger weights win and distance breaks
// ties.
export function nearestTargetInCone(enemy, context, {
  facingDegrees,
  coneDegrees = VISION_CONE_DEGREES,
  rangeMeters = VISION_RANGE_METERS,
  occluders = null,
} = {}) {
  let best = null;
  for (const target of context.targets || []) {
    if (!Array.isArray(target.coordinate)) continue;
    if (!isWithinVisionCone(enemy.position, target.coordinate, { facingDegrees, coneDegrees, rangeMeters, occluders })) {
      continue;
    }
    const distance = distanceInMeters(enemy.position, target.coordinate);
    const weight = TARGET_PULL_WEIGHTS[target.kind] ?? 1;
    if (!best || weight > best.weight || (weight === best.weight && distance < best.distance)) {
      best = { ...target, distance, weight };
    }
  }
  return best;
}

// --- Occluded cone geometry ---------------------------------------------
//
// The visible-area polygon for a rendered vision cone: fan out rays from the
// observer across the cone, clip each ray at the nearest occluding building
// edge (or the cone's range, if nothing blocks it), and return the resulting
// ring. isWithinVisionCone above answers "can the enemy see this one point,"
// point-by-point, which is all tactical-turn.mjs's sensing needs; this
// answers "what does the enemy's whole cone look like," which only a
// renderer needs (tactical-view.mjs draws it as a filled/dashed polygon). Kept
// here rather than in the view layer because it's real geometry sharing the
// same bearing convention and occluder-ring plumbing as the rest of this
// module, not anything canvas- or MapLibre-specific — the ring it returns is
// plain [lon,lat] points, usable as a GeoJSON Polygon coordinate ring as-is
// (just append the observer point again to close it, or reuse it open for a
// canvas fill/stroke).

function buildOccluderEdges(occluders) {
  const edges = [];
  if (!Array.isArray(occluders)) return edges;
  for (const feature of occluders) {
    for (const ring of ringsOf(feature)) {
      for (let index = 0; index < ring.length - 1; index += 1) edges.push({ a: ring[index], b: ring[index + 1] });
    }
  }
  return edges;
}

// Parametric ray (eye→far)/segment (a→b) intersection, returning the ray
// fraction t in [0,1] where they cross, or null if they don't cross within
// both the ray and the segment. Same algebra as a pixel-space raycast, just
// fed lon/lat coordinates directly — segment intersection (and the t ratio
// along a straight line) is preserved under the anisotropic lon/lat scaling
// at this range, same reasoning line-of-sight.mjs relies on for
// segmentsIntersect. The epsilon is ~1e3x tighter than a typical pixel-space
// raycast's, scaled down to match lon/lat-degree-sized coordinates.
function rayEdgeT(eye, far, edgeA, edgeB) {
  const rx = far[0] - eye[0];
  const ry = far[1] - eye[1];
  const sx = edgeB[0] - edgeA[0];
  const sy = edgeB[1] - edgeA[1];
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < 1e-12) return null;
  const qpx = edgeA[0] - eye[0];
  const qpy = edgeA[1] - eye[1];
  const t = (qpx * sy - qpy * sx) / denom;
  const u = (qpx * ry - qpy * rx) / denom;
  if (t >= 0 && t <= 1 && u >= 0 && u <= 1) return t;
  return null;
}

// The occluded cone as an open fan ring: [observer, clippedRay0, clippedRay1,
// ..., clippedRayN]. `occluders` is the same GeoJSON building-feature array
// isWithinVisionCone takes. Callers that need a closed GeoJSON Polygon ring
// should append observerCoordinate once more.
export function occludedConeRing(observerCoordinate, facingDegrees, {
  coneDegrees = VISION_CONE_DEGREES,
  rangeMeters = VISION_RANGE_METERS,
  occluders = null,
} = {}) {
  const edges = buildOccluderEdges(occluders);
  const facingRad = facingDegrees * Math.PI / 180;
  const halfRad = (coneDegrees / 2) * Math.PI / 180;
  const steps = Math.max(24, Math.ceil(coneDegrees / CONE_RAYCAST_STEP_DEGREES));
  const ring = [[...observerCoordinate]];
  for (let index = 0; index <= steps; index += 1) {
    const angle = facingRad - halfRad + (2 * halfRad) * (index / steps);
    const far = coordinateAtOffsetMeters(observerCoordinate, Math.cos(angle) * rangeMeters, Math.sin(angle) * rangeMeters);
    let nearestT = 1;
    for (const edge of edges) {
      const t = rayEdgeT(observerCoordinate, far, edge.a, edge.b);
      if (t !== null && t < nearestT) nearestT = t;
    }
    ring.push([
      observerCoordinate[0] + (far[0] - observerCoordinate[0]) * nearestT,
      observerCoordinate[1] + (far[1] - observerCoordinate[1]) * nearestT,
    ]);
  }
  return ring;
}
